import React from 'react';
import { Link } from 'react-router-dom';

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const quickLinks = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/products', label: 'Products' },
  { to: '/book-service', label: 'Book service' },
  { to: '/about', label: 'About us' },
  { to: '/contact', label: 'Contact' },
];

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-400 relative z-10">
      <div className="max-w-5xl mx-auto px-6 py-14">

        {/* Top row: brand + CTA */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-10 border-b border-gray-800">
          <div>
            <h2 className="text-white text-xl font-semibold">MV Computers</h2>
            <p className="text-sm mt-1 max-w-sm">
              We're here to help with all your printer and computer needs — in-shop or at your doorstep.
            </p>
          </div>
          <Link
            to="/book-service"
            onClick={scrollToTop}
            className="self-start md:self-auto bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition whitespace-nowrap"
          >
            Book a service
          </Link>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 py-10">

          {/* Get in touch */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
              Get in touch
            </h3>
            <div className="flex flex-col gap-3 text-sm">
              <a
                href="tel:+919199221118"
                className="flex items-center gap-2.5 hover:text-white transition"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +91 91992 21118
              </a>
              <a
                href="mailto:manoj.paul@mvcomputers.in"
                className="flex items-center gap-2.5 hover:text-white transition"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                manoj.paul@mvcomputers.in
              </a>
              <a
                href="https://wa.me/919199221118"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 hover:text-white transition"
              >
                <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
              Quick links
            </h3>
            <ul className="flex flex-col gap-2.5 text-sm">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} onClick={scrollToTop} className="hover:text-white transition">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Office address + map */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
              Office address
            </h3>
            <p className="text-sm text-gray-300 mb-1">MV Computers</p>
            <p className="text-sm leading-relaxed mb-2">
              Ground Floor, Sudin Chowk Rd, near Bank of Baroda, Rajnikant Colony, Vivekanand Colony, Purnia, Bihar 854301
            </p>
            <p className="text-sm mb-4">Open Mon–Sat, 9:00 AM – 7:00 PM</p>

            <div className="relative group rounded-xl overflow-hidden border border-gray-800">
              <a
                href="https://maps.app.goo.gl/YGfVUjA9EUAVkMj18"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-10 bg-blue-500 opacity-0 group-hover:opacity-10 transition"
                aria-label="Open in Google Maps"
              />
              <iframe
                title="MV Computers Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7186.046880636135!2d87.48356439999999!3d25.7697893!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eff912f7c30005%3A0x1cf7d9fe447bb818!2sMV%20Computers!5e0!3m2!1sen!2sin!4v1781803528464!5m2!1sen!2sin"
                width="100%"
                height="160"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-5xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} MV Computers. All rights reserved.</p>
          <p>Made with care in Purnia, Bihar</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;